import type { PaginatedListParams } from './types';
import type { WorkQueueQuery } from '@/api/workQueue.api';
import type { ErpModule } from '@/api/platform.api';

/** Central query-key factory — every GET should use keys from here for deduplication. */
export const queryKeys = {
  session: {
    all: ['session'] as const,
    init: (companyId?: number | null) => ['session', 'init', companyId ?? null] as const,
  },

  ui: {
    menus: (module?: string | null) => ['ui', 'menus', module ?? null] as const,
    screenActions: (path: string) => ['ui', 'screenActions', path] as const,
    workflow: (ownerModule: string, entityType: string, entityId: number) =>
      ['ui', 'workflow', ownerModule, entityType, entityId] as const,
    dashboardWidgets: (module?: string | null) => ['ui', 'dashboardWidgets', module ?? null] as const,
    formSchema: (screenId: string) => ['ui', 'formSchema', screenId] as const,
    tableSchema: (screenId: string) => ['ui', 'tableSchema', screenId] as const,
    options: (type: string) => ['ui', 'options', type] as const,
  },

  organization: {
    me: ['organization', 'me'] as const,
  },

  reference: {
    all: ['reference', 'all'] as const,
    countries: (search?: string, page?: number) => ['reference', 'countries', search ?? '', page ?? 0] as const,
    currencies: (search?: string, page?: number) => ['reference', 'currencies', search ?? '', page ?? 0] as const,
  },

  companies: {
    accessible: ['companies', 'accessible'] as const,
    list: (params?: PaginatedListParams) => ['companies', 'list', params ?? {}] as const,
    detail: (id: number) => ['companies', id] as const,
    departments: (companyId: number) => ['companies', companyId, 'departments'] as const,
    setupStatus: ['companies', 'setup-status'] as const,
  },

  stores: {
    list: (params?: PaginatedListParams) => ['stores', 'list', params ?? {}] as const,
    active: (companyId?: number | null) => ['stores', 'active', companyId ?? null] as const,
  },

  employees: {
    list: (params?: PaginatedListParams & { hrScoped?: boolean }) =>
      ['employees', 'list', params ?? {}] as const,
    options: (params?: PaginatedListParams & { hrScoped?: boolean }) =>
      ['employees', 'options', params ?? {}] as const,
    detail: (id: number) => ['employees', id] as const,
  },

  customers: {
    list: (params?: PaginatedListParams) => ['customers', 'list', params ?? {}] as const,
    options: (params?: PaginatedListParams) => ['customers', 'options', params ?? {}] as const,
    detail: (id: number) => ['customers', id] as const,
  },

  suppliers: {
    list: (params?: PaginatedListParams) => ['suppliers', 'list', params ?? {}] as const,
    options: (params?: PaginatedListParams) => ['suppliers', 'options', params ?? {}] as const,
  },

  products: {
    list: (params?: PaginatedListParams) => ['products', 'list', params ?? {}] as const,
    options: (params?: PaginatedListParams) => ['products', 'options', params ?? {}] as const,
    formMetadata: () => ['products', 'form-metadata'] as const,
  },

  categories: {
    list: (params?: PaginatedListParams) => ['categories', 'list', params ?? {}] as const,
  },

  brands: {
    list: (params?: PaginatedListParams) => ['brands', 'list', params ?? {}] as const,
  },

  units: {
    list: (params?: PaginatedListParams) => ['units', 'list', params ?? {}] as const,
  },

  taxes: {
    list: (params?: PaginatedListParams) => ['taxes', 'list', params ?? {}] as const,
  },

  taxRules: {
    list: (params?: PaginatedListParams) => ['tax-rules', 'list', params ?? {}] as const,
  },

  storeProducts: {
    list: (params?: PaginatedListParams & { storeId?: number }) =>
      ['store-products', 'list', params ?? {}] as const,
  },

  discounts: {
    offers: (params?: PaginatedListParams) => ['discounts', 'offers', params ?? {}] as const,
    coupons: (params?: PaginatedListParams) => ['discounts', 'coupons', params ?? {}] as const,
  },

  roles: {
    list: (params?: PaginatedListParams) => ['roles', 'list', params ?? {}] as const,
    assignable: (employeeId?: number) => ['roles', 'assignable', employeeId ?? 'new'] as const,
  },

  permissions: {
    list: (params?: PaginatedListParams) => ['permissions', 'list', params ?? {}] as const,
  },

  platform: {
    features: ['platform', 'features'] as const,
    modules: ['platform', 'modules'] as const,
  },

  hr: {
    orgStructure: (companyId?: number | null) => ['hr', 'org-structure', companyId ?? null] as const,
    dashboard: ['hr', 'dashboard'] as const,
    leaveTypes: ['hr', 'leave-types'] as const,
    leavePolicies: ['hr', 'leave-policies'] as const,
    companyHolidays: (start: string, end: string) => ['hr', 'company-holidays', start, end] as const,
    holidayTypes: (includeInactive?: boolean) => ['hr', 'holiday-types', includeInactive ?? true] as const,
    successionPlans: () => ['hr', 'succession-plans'] as const,
    travel: () => ['hr', 'travel'] as const,
    trainingCourses: () => ['hr', 'training-courses'] as const,
    employee360: (id: number) => ['hr', 'employee-360', id] as const,
    hierarchyTree: (scope: string) => ['hr', 'hierarchy-tree', scope] as const,
    mssSummary: () => ['hr', 'mss-summary'] as const,
    payrollRuns: (params?: PaginatedListParams) => ['hr', 'payroll-runs', params ?? {}] as const,
    expenseClaims: (params?: PaginatedListParams) => ['hr', 'expense-claims', params ?? {}] as const,
    attendance: (params?: PaginatedListParams) => ['hr', 'attendance', params ?? {}] as const,
    leaves: (params?: PaginatedListParams) => ['hr', 'leaves', params ?? {}] as const,
    recruitment: {
      dashboard: ['hr', 'recruitment', 'dashboard'] as const,
      workQueue: ['hr', 'recruitment', 'work-queue'] as const,
      pipeline: (search?: string) => ['hr', 'recruitment', 'pipeline', search ?? ''] as const,
      analytics: () => ['hr', 'recruitment', 'analytics'] as const,
      jobs: () => ['hr', 'recruitment', 'jobs'] as const,
      requisitions: () => ['hr', 'recruitment', 'requisitions'] as const,
      manpowerRequests: () => ['hr', 'recruitment', 'manpower'] as const,
    },
    fieldPermissions: () => ['hr', 'field-permissions'] as const,
    countryPacks: () => ['hr', 'country-packs'] as const,
    shiftRotations: () => ['hr', 'shift-rotations'] as const,
    compensationStructures: () => ['hr', 'compensation-structures'] as const,
    setup: (scopeVersion?: number) => ['hr', 'setup', scopeVersion ?? 0] as const,
    workforceSettings: (scopeVersion?: number) => ['hr', 'workforce-settings', scopeVersion ?? 0] as const,
    workforceDashboard: () => ['hr', 'workforce-dashboard'] as const,
    employmentTypes: () => ['hr', 'employment-types'] as const,
    employeeWorkforce: (employeeId: number, year: number, month: number) =>
      ['hr', 'employee-workforce', employeeId, year, month] as const,
    calendarOverrides: (employeeId: number, start: string, end: string) =>
      ['hr', 'calendar-overrides', employeeId, start, end] as const,
    appraisalCycles: () => ['hr', 'appraisal-cycles'] as const,
    appraisalReviews: (cycleId: number) => ['hr', 'appraisal-reviews', cycleId] as const,
    loans: (params?: PaginatedListParams) => ['hr', 'loans', params ?? {}] as const,
    assetRequests: (params?: PaginatedListParams) => ['hr', 'asset-requests', params ?? {}] as const,
    shifts: () => ['hr', 'shifts'] as const,
    shiftAssignments: (employeeId?: number | null) => ['hr', 'shift-assignments', employeeId ?? 'all'] as const,
    overtime: () => ['hr', 'overtime'] as const,
    roster: (start?: string, end?: string) => ['hr', 'roster', start ?? '', end ?? ''] as const,
    payrollFormulas: (countryCode?: string) => ['hr', 'payroll-formulas', countryCode ?? ''] as const,
    essProfile: () => ['hr', 'ess-profile'] as const,
    essWorkspace: (start: string, end: string) => ['hr', 'ess-workspace', start, end] as const,
    leaveBalances: (employeeId: number) => ['hr', 'leave-balances', employeeId] as const,
    publicCareersJobs: (orgId: number) => ['hr', 'public-careers-jobs', orgId] as const,
    devices: () => ['hr', 'devices'] as const,
    workforceAnalytics: () => ['hr', 'workforce-analytics'] as const,
    masterData: () => ['hr', 'master-data'] as const,
    employee360List: () => ['hr', 'employee-360-list'] as const,
    attendanceCalendar: (start: string, end: string) => ['hr', 'attendance-calendar', start, end] as const,
    leaveCalendar: (start: string, end: string) => ['hr', 'leave-calendar', start, end] as const,
    employeeScheduleCalendar: (start: string, end: string, employeeIdsKey: string) =>
      ['hr', 'employee-schedule-calendar', start, end, employeeIdsKey] as const,
    filteredEmployees: (params?: PaginatedListParams) => ['hr', 'filtered-employees', params ?? {}] as const,
  },

  dashboard: {
    master: ['dashboard', 'master'] as const,
    org: (storeId?: number | null) => ['dashboard', 'org', storeId ?? null] as const,
    orgFull: (storeId?: number | null) => ['dashboard', 'org-full', storeId ?? null] as const,
    orgStores: ['dashboard', 'org-stores'] as const,
    company: ['dashboard', 'company'] as const,
    hr: ['dashboard', 'hr'] as const,
    crm: ['dashboard', 'crm'] as const,
    finance: ['dashboard', 'finance'] as const,
    desk: ['dashboard', 'desk'] as const,
    procurement: ['dashboard', 'procurement'] as const,
    projects: ['dashboard', 'projects'] as const,
    warehouse: ['dashboard', 'warehouse'] as const,
    leadgen: ['dashboard', 'leadgen'] as const,
    marketing: ['dashboard', 'marketing'] as const,
    social: {
      full: ['dashboard', 'social', 'full'] as const,
      summary: ['dashboard', 'social', 'summary'] as const,
      monitoring: ['dashboard', 'social', 'monitoring'] as const,
      analytics: ['dashboard', 'social', 'analytics'] as const,
    },
  },

  notifications: {
    list: (params: { page: number; size: number; module?: ErpModule }) =>
      ['notifications', 'list', params] as const,
    unread: (module?: ErpModule) => ['notifications', 'unread', module ?? 'all'] as const,
    preferences: ['notifications', 'preferences'] as const,
  },

  workQueue: {
    list: (params: WorkQueueQuery) => ['work-queue', params] as const,
  },

  projects: {
    list: (params?: PaginatedListParams) => ['projects', 'list', params ?? {}] as const,
    options: (params?: PaginatedListParams) => ['projects', 'options', params ?? {}] as const,
    issues: (params?: PaginatedListParams) => ['projects', 'issues', params ?? {}] as const,
    sprints: (params?: PaginatedListParams) => ['projects', 'sprints', params ?? {}] as const,
    timesheets: (params?: PaginatedListParams) => ['projects', 'timesheets', params ?? {}] as const,
    tasks: (params?: PaginatedListParams & { projectId?: number }) => ['projects', 'tasks', params ?? {}] as const,
    portfolios: (params?: PaginatedListParams) => ['projects', 'portfolios', params ?? {}] as const,
    analyticsDashboard: () => ['projects', 'analytics-dashboard'] as const,
    budgetSummary: (projectId: number) => ['projects', 'budget-summary', projectId] as const,
    automation: () => ['projects', 'automation'] as const,
    boards: (projectId?: number) => ['projects', 'boards', projectId ?? 'all'] as const,
    calendar: (projectId?: number) => ['projects', 'calendar', projectId ?? 'all'] as const,
    gantt: (projectId?: number) => ['projects', 'gantt', projectId ?? 'all'] as const,
    reports: () => ['projects', 'reports'] as const,
    resources: (projectId?: number) => ['projects', 'resources', projectId ?? 'all'] as const,
    setup: () => ['projects', 'setup'] as const,
    workload: () => ['projects', 'workload'] as const,
    clientPortal: (token: string) => ['projects', 'client-portal', token] as const,
  },

  social: {
    accounts: ['social', 'accounts'] as const,
    campaigns: (params?: PaginatedListParams) => ['social', 'campaigns', params ?? {}] as const,
    listeningMatches: (params?: PaginatedListParams) => ['social', 'listening-matches', params ?? {}] as const,
    reviews: (params?: PaginatedListParams) => ['social', 'reviews', params ?? {}] as const,
    reviewsSummary: () => ['social', 'reviews-summary'] as const,
    mediaFolders: () => ['social', 'media-folders'] as const,
    mediaAssets: (params?: PaginatedListParams & { folderId?: number }) =>
      ['social', 'media-assets', params ?? {}] as const,
    posts: (params?: PaginatedListParams) => ['social', 'posts', params ?? {}] as const,
    automationRules: () => ['social', 'automation-rules'] as const,
    brands: () => ['social', 'brands'] as const,
    listeningKeywords: () => ['social', 'listening-keywords'] as const,
    competitors: () => ['social', 'competitors'] as const,
    integrationConfigs: () => ['social', 'integration-configs'] as const,
    connectionOverview: () => ['social', 'connection-overview'] as const,
    connectionLogs: (page: number, size: number) => ['social', 'connection-logs', page, size] as const,
    calendar: (from: string, to: string) => ['social', 'calendar', from, to] as const,
    analyticsCenter: () => ['social', 'analytics-center'] as const,
    socialReports: () => ['social', 'reports'] as const,
    teams: () => ['social', 'teams'] as const,
    platformSetup: (platform?: string) => ['social', 'platform-setup', platform ?? ''] as const,
    conversations: (params?: PaginatedListParams & { platform?: string }) =>
      ['social', 'conversations', params ?? {}] as const,
    messages: (conversationId: number, params?: PaginatedListParams) =>
      ['social', 'messages', conversationId, params ?? {}] as const,
    tags: () => ['social', 'tags'] as const,
    internalNotes: (entityType: string, entityId: number) =>
      ['social', 'internal-notes', entityType, entityId] as const,
  },

  warehouse: {
    openPutaway: () => ['warehouse', 'open-putaway'] as const,
    openPicks: () => ['warehouse', 'open-picks'] as const,
    openTransfers: () => ['warehouse', 'open-transfers'] as const,
    warehouses: () => ['warehouse', 'warehouses'] as const,
    hierarchy: (warehouseId: number) => ['warehouse', 'hierarchy', warehouseId] as const,
    laborMetrics: (day: string) => ['warehouse', 'labor-metrics', day] as const,
    crossDock: () => ['warehouse', 'cross-dock'] as const,
    cycleCounts: () => ['warehouse', 'cycle-counts'] as const,
    dock: () => ['warehouse', 'dock'] as const,
    mobileTasks: () => ['warehouse', 'mobile-tasks'] as const,
    picks: () => ['warehouse', 'picks'] as const,
    transfers: () => ['warehouse', 'transfers'] as const,
    waves: () => ['warehouse', 'waves'] as const,
  },

  pos: {
    dailySummary: (storeId: number, date?: string) => ['pos', 'daily', storeId, date ?? 'today'] as const,
    analyticsFull: (storeId: number, date: string) => ['pos', 'analytics-full', storeId, date] as const,
    activeShift: (storeId: number) => ['pos', 'shift', 'active', storeId] as const,
    activeShiftByCounter: (counterId: number) => ['pos', 'shift', 'active-by-counter', counterId] as const,
    counters: (storeId: number) => ['pos', 'counters', storeId] as const,
    customerDisplay: (storeId: number) => ['pos', 'customer-display', storeId] as const,
  },

  inventory: {
    list: (params: PaginatedListParams & { storeId?: number }) => ['inventory', 'list', params] as const,
    transactions: (params?: PaginatedListParams & { storeId?: number }) =>
      ['inventory', 'transactions', params ?? {}] as const,
    transfers: (params?: PaginatedListParams & { fromStoreId?: number }) =>
      ['inventory', 'transfers', params ?? {}] as const,
    backorders: (params?: PaginatedListParams & { storeId?: number }) =>
      ['inventory', 'backorders', params ?? {}] as const,
    serials: (params?: PaginatedListParams) => ['inventory', 'serials', params ?? {}] as const,
    readiness: () => ['inventory', 'readiness'] as const,
    ledger: (params?: PaginatedListParams) => ['inventory', 'ledger', params ?? {}] as const,
  },

  finance: {
    invoices: (params?: PaginatedListParams) => ['finance', 'invoices', params ?? {}] as const,
    payments: (params?: PaginatedListParams) => ['finance', 'payments', params ?? {}] as const,
    expenses: (params?: PaginatedListParams) => ['finance', 'expenses', params ?? {}] as const,
    budgets: (year: number) => ['finance', 'budgets', year] as const,
    budgetVariance: (year: number) => ['finance', 'budget-variance', year] as const,
    openPayables: () => ['finance', 'open-payables'] as const,
    paymentRuns: () => ['finance', 'payment-runs'] as const,
    deferredRevenue: () => ['finance', 'deferred-revenue'] as const,
    bankAccounts: () => ['finance', 'bank-accounts'] as const,
    bankFeeds: () => ['finance', 'bank-feeds'] as const,
    unmatchedTransactions: (accountId: number) => ['finance', 'unmatched-transactions', accountId] as const,
    workflows: () => ['finance', 'workflows'] as const,
    accounts: (params?: PaginatedListParams) => ['finance', 'accounts', params ?? {}] as const,
    journals: (params?: PaginatedListParams) => ['finance', 'journals', params ?? {}] as const,
    pendingApprovals: () => ['finance', 'pending-approvals'] as const,
    fiscalPeriods: () => ['finance', 'fiscal-periods'] as const,
    trialBalance: () => ['finance', 'trial-balance'] as const,
    accountingEvents: (params?: PaginatedListParams) => ['finance', 'accounting-events', params ?? {}] as const,
    auditTrail: () => ['finance', 'audit-trail'] as const,
    cashForecast: (days: number) => ['finance', 'cash-forecast', days] as const,
    healthCenter: () => ['finance', 'health-center'] as const,
    reportsSummary: () => ['finance', 'reports-summary'] as const,
    closeRuns: () => ['finance', 'close-runs'] as const,
    closeTasks: (runId: number) => ['finance', 'close-tasks', runId] as const,
    businessRules: () => ['finance', 'business-rules'] as const,
    automationCenter: () => ['finance', 'automation-center'] as const,
    collectionsCenter: () => ['finance', 'collections-center'] as const,
    compliance: () => ['finance', 'compliance'] as const,
    documentCaptures: () => ['finance', 'document-captures'] as const,
    assets: () => ['finance', 'assets'] as const,
    taxes: () => ['finance', 'taxes'] as const,
    taxGroups: () => ['finance', 'tax-groups'] as const,
    finWorkflows: () => ['finance', 'fin-workflows'] as const,
    dunningNotices: () => ['finance', 'dunning-notices'] as const,
    customReports: () => ['finance', 'custom-reports'] as const,
    setupWizard: () => ['finance', 'setup-wizard'] as const,
    analytics: () => ['finance', 'analytics'] as const,
    integrations: () => ['finance', 'integrations'] as const,
    treasury: () => ['finance', 'treasury'] as const,
  },

  procurement: {
    purchaseOrders: (params?: PaginatedListParams) =>
      ['procurement', 'purchase-orders', params ?? {}] as const,
    purchaseRequests: (params?: PaginatedListParams) =>
      ['procurement', 'purchase-requests', params ?? {}] as const,
    goodsReceipts: (params?: PaginatedListParams) =>
      ['procurement', 'goods-receipts', params ?? {}] as const,
    rfqs: (params?: PaginatedListParams) => ['procurement', 'rfqs', params ?? {}] as const,
    contracts: (params?: PaginatedListParams) => ['procurement', 'contracts', params ?? {}] as const,
    vendorBills: (params?: PaginatedListParams) => ['procurement', 'vendor-bills', params ?? {}] as const,
    vendorPayments: (params?: PaginatedListParams) => ['procurement', 'vendor-payments', params ?? {}] as const,
    approvalWorkflows: () => ['procurement', 'approval-workflows'] as const,
    quotationComparison: (rfqId: number) => ['procurement', 'quotation-comparison', rfqId] as const,
    catalogs: (publishedOnly?: boolean) => ['procurement', 'catalogs', publishedOnly ?? false] as const,
    vendorPortalDashboard: () => ['procurement', 'vendor-portal-dashboard'] as const,
  },

  crm: {
    leads: (params?: PaginatedListParams) => ['crm', 'leads', params ?? {}] as const,
    deals: (params?: PaginatedListParams) => ['crm', 'deals', params ?? {}] as const,
    dealsKanban: (params?: PaginatedListParams) => ['crm', 'deals', 'kanban', params ?? {}] as const,
    contacts: (params?: PaginatedListParams) => ['crm', 'contacts', params ?? {}] as const,
    companies: (params?: PaginatedListParams) => ['crm', 'companies', params ?? {}] as const,
    campaigns: (params?: PaginatedListParams) => ['crm', 'campaigns', params ?? {}] as const,
    openTasks: () => ['crm', 'open-tasks'] as const,
    territories: () => ['crm', 'territories'] as const,
    defaultPipeline: () => ['crm', 'default-pipeline'] as const,
    setup: () => ['crm', 'setup'] as const,
    customer360: (customerId: number) => ['crm', 'customer-360', customerId] as const,
    reports: () => ['crm', 'reports'] as const,
  },

  desk: {
    tickets: (params?: PaginatedListParams) => ['desk', 'tickets', params ?? {}] as const,
    cannedResponses: () => ['desk', 'canned-responses'] as const,
    kbCategories: () => ['desk', 'kb-categories'] as const,
    kbArticles: () => ['desk', 'kb-articles'] as const,
    inboxConversations: () => ['desk', 'inbox-conversations'] as const,
    inboxMessages: (conversationId: number) => ['desk', 'inbox-messages', conversationId] as const,
    statuses: () => ['desk', 'statuses'] as const,
    ticketDetail: (ticketId: number, userId?: number | null) =>
      ['desk', 'ticket-detail', ticketId, userId ?? null] as const,
    ticketAttachments: (ticketId: number) => ['desk', 'ticket-attachments', ticketId] as const,
    deskWorkflows: () => ['desk', 'desk-workflows'] as const,
    workflowTransitions: (workflowId: number) => ['desk', 'workflow-transitions', workflowId] as const,
    automation: () => ['desk', 'automation'] as const,
    boards: () => ['desk', 'boards'] as const,
    reports: () => ['desk', 'reports'] as const,
    setup: () => ['desk', 'setup'] as const,
    support360: (customerId: number) => ['desk', 'support-360', customerId] as const,
  },

  approvals: {
    metadata: () => ['approvals', 'metadata'] as const,
    inbox: (params?: Record<string, unknown>) => ['approvals', 'inbox', params ?? {}] as const,
    dashboard: (module?: string) => ['approvals', 'dashboard', module ?? 'all'] as const,
    workflows: (module?: string) => ['approvals', 'workflows', module ?? 'all'] as const,
    matrix: () => ['approvals', 'matrix'] as const,
  },

  leadgen: {
    sources: () => ['leadgen', 'sources'] as const,
    consent: () => ['leadgen', 'consent'] as const,
    visitors: () => ['leadgen', 'visitors'] as const,
    campaigns: () => ['leadgen', 'campaigns'] as const,
    forms: () => ['leadgen', 'forms'] as const,
    landingPages: () => ['leadgen', 'landing-pages'] as const,
    scoringRules: () => ['leadgen', 'scoring-rules'] as const,
    gradeConfig: () => ['leadgen', 'grade-config'] as const,
    routingRules: () => ['leadgen', 'routing-rules'] as const,
    reports: () => ['leadgen', 'reports'] as const,
    abExperiments: () => ['leadgen', 'ab-experiments'] as const,
    webhooks: () => ['leadgen', 'webhooks'] as const,
    publicForm: (publicKey: string) => ['leadgen', 'public-form', publicKey] as const,
  },

  organizations: {
    list: (params?: PaginatedListParams) => ['organizations', 'list', params ?? {}] as const,
  },

  marketing: {
    segments: () => ['marketing', 'segments'] as const,
    campaigns: () => ['marketing', 'campaigns'] as const,
    templates: () => ['marketing', 'templates'] as const,
    journeys: () => ['marketing', 'journeys'] as const,
    lifecyclePresets: () => ['marketing', 'lifecycle-presets'] as const,
    attribution: (campaignId: number, model?: string) =>
      ['marketing', 'attribution', campaignId, model ?? ''] as const,
  },

  master: {
    users: (params?: PaginatedListParams & { masterOnly?: boolean; activeOnly?: boolean }) =>
      ['master', 'users', params ?? {}] as const,
    customPlans: () => ['master', 'custom-plans'] as const,
    referenceData: () => ['master-data', 'all'] as const,
    platformConfig: () => ['master', 'platform-config'] as const,
  },

  subscriptions: {
    plans: () => ['subscriptions', 'plans'] as const,
    current: () => ['subscriptions', 'current'] as const,
    paymentHistory: () => ['subscriptions', 'payment-history'] as const,
    usage: () => ['subscriptions', 'usage'] as const,
    customPlanRequests: () => ['subscriptions', 'custom-plan-requests'] as const,
  },

  billing: {
    heldBills: (storeId: number) => ['billing', 'held-bills', storeId] as const,
    invoices: (params?: PaginatedListParams & { storeId?: number; from?: string; to?: string }) =>
      ['billing', 'invoices', params ?? {}] as const,
    overview: () => ['billing', 'overview'] as const,
    modulePlans: (moduleCode: string) => ['billing', 'module-plans', moduleCode] as const,
  },

  portal: {
    dashboard: () => ['portal', 'dashboard'] as const,
    tickets: () => ['portal', 'tickets'] as const,
  },

  identity: {
    portalDashboard: () => ['identity', 'portal-dashboard'] as const,
  },

  publicPortal: {
    candidate: (token: string) => ['public', 'candidate-portal', token] as const,
  },

  productAlerts: {
    list: (storeId: number) => ['product-alerts', 'list', storeId] as const,
  },

  orgReports: {
    tab: (tab: string, from: string, to: string, storeId?: number) =>
      ['org-reports', tab, from, to, storeId ?? null] as const,
  },

  moduleWorkspace: {
    settings: (moduleCode: string) => ['module-workspace', 'settings', moduleCode] as const,
  },

  masterAnalytics: {
    summary: () => ['master', 'analytics'] as const,
    plans: () => ['master', 'plans'] as const,
  },
} as const;
